import React, { useState, useEffect, useMemo } from 'react';
import { useGamification } from '../hooks/GamificationContext';

export function PfandspendeForm({ user, onSubmitted }) {
  const { applyGamificationPayload } = useGamification() || {};
  // ---------------- State ----------------
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [foto, setFoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status] = useState('offen');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Ablauf-Optionen
  const ablaufOptionen = [
    { label: "15 Minuten", value: 15 },
    { label: "30 Minuten", value: 30 },
    { label: "1 Stunde", value: 60 },
    { label: "2 Stunden", value: 120 },
    { label: "Benutzerdefiniert", value: "custom" }
  ];
  const [ablaufIn, setAblaufIn] = useState(60);
  const [customMin, setCustomMin] = useState(0);

  // ---------------- Helpers ----------------
  const normCoord = (v) => {
    if (v === null || v === undefined) return '';
    return String(v).trim().replace(',', '.');
  };
  const toFloat = (v) => {
    const n = parseFloat(normCoord(v));
    return Number.isFinite(n) ? n : NaN;
  };

  function formatLocalMySQL(minutesAhead = 60) {
    const d = new Date();
    d.setMinutes(d.getMinutes() + Number(minutesAhead || 60));
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  // Ablauf‑Vorschau
  const ablaufPreview = useMemo(() => {
    const m = (ablaufIn === 'custom' ? parseInt(customMin || 0, 10) : ablaufIn) || 60;
    const d = new Date();
    d.setMinutes(d.getMinutes() + m);
    return d.toLocaleString();
  }, [ablaufIn, customMin]);

  // Geoposition vorausfüllen
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      setLatitude(String(pos.coords.latitude));
      setLongitude(String(pos.coords.longitude));
    }, () => {});
  }, []);

 async function handleGeocode() {
  if (!address?.trim()) return;
  try {
    const res = await fetch(`/wp-json/pfandpirat/v1/geocode?address=${encodeURIComponent(address)}`, {
      headers: { 'X-WP-Nonce': window.pfandpiratData?.nonce || '' }
    });
    if (!res.ok) {
      alert('Geocoding fehlgeschlagen (Server antwortete nicht OK).');
      return;
    }
    const data = await res.json();
    if (data?.lat && data?.lon) {
      setLatitude(String(data.lat));
      setLongitude(String(data.lon));
      setErrors(e => ({ ...e, latitude: null, longitude: null }));
    } else {
      alert('Adresse nicht gefunden.');
    }
  } catch (e) {
    alert('Geocoding-Fehler: ' + e.message);
  }
}


  function handleUseGPS() {
    if (!navigator.geolocation) {
      alert('Geolocation wird von deinem Gerät/Browser nicht unterstützt.');
      return;
    }
    navigator.geolocation.getCurrentPosition(pos => {
      setLatitude(String(pos.coords.latitude));
      setLongitude(String(pos.coords.longitude));
      setErrors(e => ({ ...e, latitude: null, longitude: null }));
    }, () => alert('Konnte Standort nicht ermitteln.'));
  }

  function handleFotoChange(e) {
    const file = e.target.files?.[0];
    setFoto(file || null);
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  }

  // ---------------- Validation ----------------
  function validate() {
    const errs = {};
    if (!titel.trim()) errs.titel = 'Bitte Titel angeben.';
    if (!beschreibung.trim()) errs.beschreibung = 'Bitte kurze Beschreibung angeben.';
    const latF = toFloat(latitude), lonF = toFloat(longitude);
    if (!Number.isFinite(latF) || latF < -90 || latF > 90) errs.latitude = 'Latitude ungültig.';
    if (!Number.isFinite(lonF) || lonF < -180 || lonF > 180) errs.longitude = 'Longitude ungültig.';
    if (ablaufIn === 'custom') {
      const cm = parseInt(customMin, 10);
      if (!cm || cm < 1 || cm > 1440) errs.customMin = 'Bitte 1–1440 Minuten angeben.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ---------------- Submit ----------------
  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    setLoading(true);

    const latF = toFloat(latitude);
    const lonF = toFloat(longitude);

    try {
      // 1) Beitrag anlegen
      const createResp = await fetch('/wp-json/wp/v2/pfand_spende', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window.pfandpiratData.nonce
        },
        body: JSON.stringify({ title: titel, status: 'publish' })
      });
      const created = await createResp.json();
      if (!created?.id) throw new Error(created?.message || 'Fehler beim Anlegen.');

      // 2) ACF‑Felder inkl. Ablaufdatum
      const minutes = (ablaufIn === 'custom' ? customMin : ablaufIn);
      await fetch(`/wp-json/wp/v2/pfand_spende/${created.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window.pfandpiratData.nonce
        },
        body: JSON.stringify({
          acf: {
            beschreibung: beschreibung.trim(),
            latitude: String(latF),
            longitude: String(lonF),
            status: status,
            ablaufdatum: formatLocalMySQL(minutes)
          }
        })
      });

      // 3) Optional: Bild hochladen
      if (foto) {
        const formData = new FormData();
        formData.append('file', foto);
        formData.append('post', created.id);
        const uploadResp = await fetch('/wp-json/wp/v2/media', {
          method: 'POST',
          headers: { 'X-WP-Nonce': window.pfandpiratData.nonce },
          body: formData
        });
        const uploaded = await uploadResp.json();
        if (uploaded?.id) {
          await fetch(`/wp-json/wp/v2/pfand_spende/${created.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-WP-Nonce': window.pfandpiratData.nonce
            },
            body: JSON.stringify({ featured_media: uploaded.id })
          });
        }
      }

      try {
        const xpResp = await fetch(`/wp-json/pfandpirat/v1/pfandspenden/${created.id}/mark-xp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.pfandpiratData.nonce
          }
        });
        const xpPayload = await xpResp.json().catch(() => null);
        if (xpResp.ok && xpPayload?.xp_toast && typeof applyGamificationPayload === 'function') {
          applyGamificationPayload(xpPayload, {
            emitXpToast: true,
            event: xpPayload.xp_toast.event || xpPayload.event || 'mark_pfandspende',
            awarded: xpPayload.xp_toast.awarded ?? xpPayload.awarded ?? 0,
            toastLabel: xpPayload.xp_toast.label || 'Pfandspende markiert'
          });
        }
      } catch (xpError) {}

      // 4) Erfolg → weiter
      if (typeof onSubmitted === 'function') onSubmitted(created);
      else if (created?.link) window.location.href = created.link;
      else window.location.href = '/pfand_spende/';

    } catch (err) {
      alert('Fehler: ' + err.message);
      setLoading(false);
    }
  }

  // ---------------- Render ----------------
  return (
    <form onSubmit={handleSubmit} className="card form">
      <h2 className="form__title">Pfandspende erstellen</h2>

      {/* Was? */}
      <section className="form__section">
        <h3 className="form__section-title">Was bietest du an?</h3>
        <div className="grid-2">
          <div className="field">
            <label className="label">Titel<span className="req">*</span></label>
            <input
              className={`input ${errors.titel ? 'input--error' : ''}`}
              type="text"
              value={titel}
              onChange={e => setTitel(e.target.value)}
              placeholder="z. B. Kiste im Hinterhof"
              required
            />
            {errors.titel && <div className="help help--error">{errors.titel}</div>}
          </div>

          <div className="field">
            <label className="label">Kurzbeschreibung<span className="req">*</span></label>
            <textarea
              className={`textarea ${errors.beschreibung ? 'input--error' : ''}`}
              rows={3}
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              placeholder="z. B. 10 Flaschen, hölzerne Kiste links am Tor"
              required
            />
            <div className="help">Je genauer, desto besser auffindbar.</div>
            {errors.beschreibung && <div className="help help--error">{errors.beschreibung}</div>}
          </div>
        </div>
      </section>

      {/* Wo? */}
      <section className="form__section">
        <h3 className="form__section-title">Wo liegt die Spende?</h3>

        <div className="field">
          <label className="label">Adresse (optional)</label>
          <div className="row">
            <input
              className="input"
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Straße, Hausnummer, PLZ, Ort"
            />
            <button type="button" className="btn btn--ghost" onClick={handleGeocode}>Adresse → Koordinaten</button>
            <button type="button" className="btn btn--ghost" onClick={handleUseGPS}>Standort übernehmen</button>
          </div>
          <div className="help">Nutze wahlweise Adresse oder „Standort übernehmen“. Am Ende brauchen wir gültige Koordinaten.</div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label className="label">Latitude<span className="req">*</span></label>
            <input
              className={`input ${errors.latitude ? 'input--error' : ''}`}
              inputMode="decimal"
              pattern="-?[0-9]*[.,]?[0-9]+"
              placeholder="z. B. 51.0342356"
              value={latitude}
              onChange={e => setLatitude(e.target.value)}
              required
            />
            {errors.latitude && <div className="help help--error">{errors.latitude}</div>}
          </div>

          <div className="field">
            <label className="label">Longitude<span className="req">*</span></label>
            <input
              className={`input ${errors.longitude ? 'input--error' : ''}`}
              inputMode="decimal"
              pattern="-?[0-9]*[.,]?[0-9]+"
              placeholder="z. B. 13.7163544"
              value={longitude}
              onChange={e => setLongitude(e.target.value)}
              required
            />
            {errors.longitude && <div className="help help--error">{errors.longitude}</div>}
          </div>
        </div>
      </section>

      {/* Wie lange? */}
      <section className="form__section">
        <h3 className="form__section-title">Wie lange gilt die Spende?</h3>
        <div className="radio-group" role="radiogroup" aria-label="Ablaufzeit wählen">
          {ablaufOptionen.map(opt => (
            <label key={opt.value} className="radio">
              <input
                type="radio"
                name="ablauf"
                checked={ablaufIn === opt.value}
                onChange={() => setAblaufIn(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>

        {ablaufIn === 'custom' && (
          <div className="field" style={{ maxWidth: 240 }}>
            <label className="label">Minuten (1–1440)</label>
            <input
              className={`input ${errors.customMin ? 'input--error' : ''}`}
              type="number"
              min={1}
              max={1440}
              value={customMin}
              onChange={e => setCustomMin(e.target.value)}
              placeholder="z. B. 90"
            />
            {errors.customMin && <div className="help help--error">{errors.customMin}</div>}
          </div>
        )}

        <div className="hint">
          Läuft voraussichtlich ab am: <strong>{ablaufPreview}</strong>
        </div>
      </section>

      {/* Foto */}
      <section className="form__section">
        <h3 className="form__section-title">Optional: Foto hinzufügen</h3>
        <div className="row">
          <input type="file" accept="image/*" onChange={handleFotoChange} />
          {preview && (
            <img
              src={preview}
              alt="Foto‑Vorschau"
              className="photo-preview"
            />
          )}
        </div>
        <div className="help">Ein Foto hilft beim Finden (z. B. Umgebung, Kiste, Schild).</div>
      </section>

      {/* Submit */}
      <div className="actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={loading}
        >
          {loading ? 'Wird gesendet …' : 'Pfand melden'}
        </button>
      </div>
    </form>
  );
}
